import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { dateObjToLocalZone, trimString, trimHash } from '../../../utils/converter';
import { useContext, useState } from 'react';
import { appContext } from '../../../AppContext';
import { useLocation, useNavigate } from 'react-router-dom';
import EditFileLabel from './EditFileLabel';
import ValidationResult from './ValidationResult';
import { addNotValidated } from '../../../utils/history/addNotValidated';
import { hashExistInFiles } from '../../../utils/fileHelpers';
import axios from 'axios';
import { API } from '../../../config/api';
import { v4 as uuidv4 } from 'uuid';
import LoadingDots from '../../Animations/LoadingDots';
import { toast } from 'react-toastify';

const TableRows = ({ data, getHistory, handleDelete }) => {
  const { files, setFiles, validationComplete, validateState, setValidationComplete } =
    useContext(appContext);

  const location = useLocation();
  const navigate = useNavigate();

  // Function to add an uploaded file to the DragNDrop box.
  const handleNotValidated = async (hash) => {
    await addNotValidated(hash, setFiles, validationComplete, validateState, setValidationComplete);
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const handleDownloadProof = async (id, fileName) => {
    try {
      const response = await axios.get(API.TIMESTAMP.get.proof(id), {
        withCredentials: true,
        responseType: 'blob', // Important for file downloads
      });

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const baseFileName = fileName.split('.')[0];
      link.setAttribute('download', `${baseFileName}-proof.json`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading proof:', error);
    }
  };

  return (
    <tbody className="text-[14px] font-normal">
      {data &&
        data.map((item, i) => (
          <tr key={i} className="bg-core-black-contrast h-[66px]">
            <td className="p-4 align-middle">
              <p>{trimString(item.FILE_NAME)}</p>
            </td>
            <td className="p-4 align-middle">
              <EditFileLabel
                historyItem={item}
                indexOfHistoryItem={i}
                getHistory={getHistory}
                data={data}
              />
            </td>
            <td className="p-4 align-middle">{trimHash(item.HASH)}</td>
            <td className="p-4 align-middle">
              {item.DATECREATED && (
                <>
                  <p>{dateObjToLocalZone(item.DATECREATED).split(' ')[0]}</p>
                  <p className="text-core-grey-60">
                    {dateObjToLocalZone(item.DATECREATED).split(' ')[1]}
                  </p>
                </>
              )}
            </td>
            <td className="p-4 align-middle">
              {item.PROOF === '[ERROR]' && !item.ONCHAIN ? 'No' : item.ONCHAIN ? 'Yes' : '-'}
            </td>

            <td className="p-4 align-middle">
              {item.PROOF === '[ERROR]' ? (
                <span className="text-red-500 text-xs">
                  Error during stamping. Please upload this stamp again.
                </span>
              ) : item.PROOF ? (
                <button
                  onClick={() => handleDownloadProof(item.ID, item.FILE_NAME)}
                  className="cursor-pointer hover:text-orange"
                >
                  <DownloadIcon />
                </button>
              ) : (
                <CheckUidButton getHistory={getHistory} item={item} />
              )}
            </td>
            <td className="p-4 align-middle">
              <button
                onClick={() => handleDelete(item)}
                className="hover:text-minima-core-orange cursor-pointer"
              >
                <DeleteIcon />
              </button>
            </td>
          </tr>
        ))}
    </tbody>
  );
};
export default TableRows;

function CheckUidButton({ getHistory, item }) {
  const [loadingCheckStatus, setLoadingCheckStatus] = useState(false);

  const handleCheckUidStatus = async (uid) => {
    try {
      setLoadingCheckStatus(true);
      const response = await axios.post(
        API.TIMESTAMP.status,
        {
          requestId: uuidv4(),
          uids: [uid],
        },
        { withCredentials: true },
      );

      // console.log('response', response);
      console.log('response.data', response.data);
      if (response.data.success) {
        // Check if proof is available
        if (response.data.data && response.data.data.proof) {
          toast.success('Proof has been retrieved!', {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: 'dark',
          });
        } else {
          toast.info('Proof is not on chain yet. Please try again later.', {
            position: 'bottom-right',
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            theme: 'dark',
          });
        }

        // const data = response.data.data;
        // If data is onchain, update the status
        // if (data && data.onchain) {
        //   await axios.patch(
        //     API.TIMESTAMP.patch.status(data.uid),
        //     {
        //       STATUS: data.status,
        //       NUMBER: data.number,
        //       ONCHAIN: data.onchain,
        //       DATESTAMPED: data.datestamped,
        //       ROOT: data.root,
        //       PROOF: data.proof,
        //       ADDRESS: data.address,
        //     },
        //     { withCredentials: true },
        //   );
        // }
        // Refresh the history data to show updated status
      } else if (response.data.code === 'STAMP_ERROR') {
        setLoadingCheckStatus(false);
        toast.error('Something went wrong during stamping. Please upload stamp again.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      }
      if (response.data.message === 'Not yet') {
        await new Promise((resolve) => setTimeout(resolve, 10000)); // 10 second timeout
        setLoadingCheckStatus(false);
        toast.info('Proof is not on chain yet. Please try again later.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      }
      getHistory();
    } catch (error: any) {
      console.error('Error checking UID status:', error);
      setLoadingCheckStatus(false);

      // Handle different error scenarios
      if (error.response?.status === 401) {
        toast.error('Could not check status - Authentication required. Please log in again.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      }

      // else if (error.response?.status === 429) {
      //   toast.error('Could not check status - API usage limit exceeded. Please try again later.', {
      //     position: 'bottom-right',
      //     autoClose: 5000,
      //     hideProgressBar: true,
      //     closeOnClick: true,
      //     pauseOnHover: true,
      //     draggable: true,
      //     theme: 'dark',
      //   });
      // }
      else if (error.response?.status === 403) {
        toast.error('Could not check status - API key missing or invalid.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      } else {
        toast.error('Could not check status - An error occurred. Please try again.', {
          position: 'bottom-right',
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: 'dark',
        });
      }
    }
  };

  return (
    <>
      {loadingCheckStatus ? (
        <div className="w-[24px] h-[24px] ml-1 transition-all duration-300 ease-in-out">
          <LoadingDots spinningRadius={10} heightWidthPx="24px" />
        </div>
      ) : (
        <button
          onClick={() => handleCheckUidStatus(item.UID)}
          disabled={loadingCheckStatus}
          className={`p-2 bg-minima-orange-2023 hover:bg-minima-orange-light text-black font-medium transition-all duration-300 ease-in-out`}
        >
          Check status
        </button>
      )}
    </>
  );
}
